import { useCallback, useState } from 'react';
import { LoadEventConfigUseCase } from '@/application/use-cases/LoadEventConfigUseCase';
import type { LoadEventConfigResult } from '@/application/use-cases/LoadEventConfigUseCase';
import { HttpEventConfigRepository } from '@/infrastructure/repositories/HttpEventConfigRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';

const loadEventConfigUseCase = new LoadEventConfigUseCase(new HttpEventConfigRepository());

type EventConfigStatus = 'idle' | 'loading' | 'error';

interface UseEventConfigResult {
  status: EventConfigStatus;
  /** `null` enquanto carrega, quando a falha foi sessão expirada (quem
   * chama troca de tela) ou quando foi falha de rede sem cache (`offline`
   * — a `ConfigLoadingScreen` já cobre esse estado com o aviso "Aguardando
   * conexão...", nunca uma mensagem de erro genérica — achado do Tech
   * Lead, review do PR #85). */
  errorMessage: string | null;
  loadConfig: (session: DeviceSession) => Promise<LoadEventConfigResult>;
}

/**
 * Encapsula o carregamento da configuração de ativação do evento (HUB-235):
 * `GET /events/:id/config`, autenticado com a sub-sessão de dispositivo
 * emitida pelo PIN. Substitui `useEventConsent` — a mesma chamada agora
 * também é a fonte de `activation_config` (antes vinda do arquivo estático
 * `/config/event.json`), sem duplicar requisição.
 */
export function useEventConfig(): UseEventConfigResult {
  const [status, setStatus] = useState<EventConfigStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadConfig = useCallback(
    async (session: DeviceSession): Promise<LoadEventConfigResult> => {
      setStatus('loading');
      setErrorMessage(null);

      const result = await loadEventConfigUseCase.execute(session);

      if (!result.success) {
        setStatus('error');
        const hasInlineMessage = !result.sessionExpired && !result.offline;
        setErrorMessage(hasInlineMessage ? result.error : null);
        return result;
      }

      setStatus('idle');
      return result;
    },
    []
  );

  return { status, errorMessage, loadConfig };
}
